import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Header added to retried requests so the interceptor knows NOT to
 * attempt another refresh cycle if the retry itself returns 401.
 */
const RETRY_HEADER = 'X-Token-Retry';

/**
 * URLs that should bypass the auth interceptor entirely.
 */
function shouldSkipAuth(url: string): boolean {
  const pingFederateUrls = [
    environment.pingFederate.authorizeUrl,
    environment.pingFederate.tokenUrl,
    environment.pingFederate.logoutUrl,
    environment.pingFederate.userinfoUrl,
  ].filter(Boolean);

  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/oauth') ||
    url.includes('/auth/pingfederate/') ||
    url.includes('microsoftonline.com') ||
    pingFederateUrls.some(pingUrl => url.startsWith(pingUrl))
  );
}

/**
 * Detect a 401 status regardless of error type.
 * With `withFetch()` most errors are still HttpErrorResponse,
 * but a plain object with `.status` is handled as a safety net.
 */
function isUnauthorized(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return error.status === 401;
  }
  if (typeof error === 'object' && error !== null && 'status' in error) {
    return (error as { status: number }).status === 401;
  }
  return false;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Skip auth for login / refresh / oauth / Microsoft identity endpoints
  if (shouldSkipAuth(req.url)) {
    return next(req);
  }

  // Attach the current token
  const token = authService.getAuthToken();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let headers = req.headers.set('X-Timezone-Id', timezone);
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }
  const authReq = req.clone({ headers });

  return next(authReq).pipe(
    catchError((error: unknown) => {
      // Only attempt refresh for 401 AND if this is not already a retry
      if (isUnauthorized(error) && !req.headers.has(RETRY_HEADER)) {
        return handleRefreshAndRetry(authService, req, next, error);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Clone the original request with the new token and a retry marker header.
 */
function cloneWithToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`).set(RETRY_HEADER, '1'),
  });
}

/**
 * Refresh the token and retry the original request.
 *
 * Uses shared refresh coordination on AuthService so that when
 * multiple requests get 401 at the same time, only ONE refresh
 * is performed and all others queue behind it.
 */
function handleRefreshAndRetry(
  authService: AuthService,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  originalError: unknown
): Observable<HttpEvent<unknown>> {
  // If a refresh is already in progress, queue behind it
  if (authService.isRefreshInProgress()) {
    return authService.getRefreshToken$().pipe(
      switchMap(newToken => {
        if (!newToken) {
          // Refresh failed while we were queued
          return throwError(() => originalError);
        }
        return next(cloneWithToken(req, newToken));
      })
    );
  }

  // We are the first 401 — start the refresh
  authService.startRefresh();

  return authService.refreshToken().pipe(
    switchMap(success => {
      if (success) {
        const newToken = authService.getAuthToken();

        if (!newToken) {
          console.error('[AuthInterceptor] Refresh succeeded but getAuthToken() is null!');
          authService.completeRefresh(null);
          authService.logout();
          return throwError(() => originalError);
        }

        // Unblock queued requests with the new token
        authService.completeRefresh(newToken);

        return next(cloneWithToken(req, newToken));
      }

      // Refresh failed
      console.warn('[AuthInterceptor] Refresh failed — logging out');
      authService.completeRefresh(null);
      authService.logout();
      return throwError(() => originalError);
    }),
    catchError(err => {
      console.error('[AuthInterceptor] Exception during refresh:', err);
      authService.completeRefresh(null);
      authService.logout();
      return throwError(() => err);
    })
  );
}
